import { ApexOptions } from 'apexcharts';
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import omit from 'lodash/omit';
import { useCallback, useMemo, useState } from 'react';
import {
  NormaliseNavData,
  normaliseNavData,
} from '@/modules/issuer-dashboard/helpers/normalizeNavData.ts';
import { useIssuedCapitalOverTimeQuery } from '@/modules/issuer-dashboard/queries/securities.ts';
import { BalanceChart, ChartEmptyState, FilterItem, InfoCard, Spinner } from 'ui';
import { GetRegisterClassesWithNAVResponse, RegisterClassWithNAVResponse } from 'api';

dayjs.extend(utc);

interface IssuedCapitalOverTimeChartProps {
  register_class_ids?: number[];
}
//
const chartOptions: Partial<ApexOptions> = {
  grid: {
    show: true,
    padding: {
      bottom: 0,
      right: 0,
    },
  },
  markers: {
    size: 6,
    hover: {
      size: 8,
    },
  },
  legend: {
    position: 'top',
    horizontalAlign: 'left',
    markers: {
      size: 8,
      offsetX: -5,
      offsetY: 0,
    },
    itemMargin: { horizontal: 8 },
  },
  stroke: {
    width: 2,
    lineCap: 'round',
  },
  colors: ['#0369A1', '#0EA5E9', '#34D399', '#FACC15', '#A855F7'],
  xaxis: {
    tickAmount: 6,
    type: 'datetime',
    labels: {
      rotate: 0,
      formatter: (date: string) => {
        return dayjs.utc(date).isValid() ? dayjs.utc(date).format('DD-MM-YYYY') : date;
      },
      style: {
        fontSize: '12px',
        fontWeight: '400',
        colors: '#A3A3A3',
      },
    },
    tooltip: {
      enabled: false,
    },
  },
};

export const IssuedCapitalOverTimeChart = ({
  register_class_ids,
}: IssuedCapitalOverTimeChartProps) => {
  const [filters, setFilters] = useState<{ date_from?: string; date_to?: string }>({});

  const dateValue = useMemo(
    () =>
      filters.date_from && filters.date_to
        ? ([dayjs(filters.date_from), dayjs(filters.date_to)] as [Dayjs, Dayjs])
        : undefined,
    [filters.date_from, filters.date_to],
  );

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              date_from: date[0].toISOString(),
              date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['date_from', 'date_to']);
      });
    },
    [setFilters],
  );

  const {
    data: dataIssuedCapitalOverTime,
    isLoading,
    isFetching,
    isError,
  } = useIssuedCapitalOverTimeQuery({
    ...filters,
    // register_class_ids: register_class_ids?.join(','),
  });

  const chartData = useMemo<NormaliseNavData[]>(() => {
    const newDate = dataIssuedCapitalOverTime?.data?.map<RegisterClassWithNAVResponse>(
      item =>
        ({
          id: item.security.id,
          class_name: item.security.class_name,
          navs: item.data?.map(nItem => ({
            changes_date: format(new Date(nItem.date), 'yyyy-MM-dd'),
            price_per_share: nItem.value,
          })),
        } as RegisterClassWithNAVResponse),
    );
    let result = normaliseNavData(
      {
        data: newDate,
      } as GetRegisterClassesWithNAVResponse,
      !dateValue?.[1],
    );

    result = result.filter(item => register_class_ids?.includes(item.id) || !register_class_ids);

    return result;
  }, [dataIssuedCapitalOverTime?.data, dateValue, register_class_ids]);

  return (
    <InfoCard
      title="Issued capital over time"
      classes={{ content: 'w-full p-0 px-6' }}
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && !isLoading && <Spinner size="md" />}
          <FilterItem
            placeholder="Date"
            type="date"
            onChange={handleChangeDate}
            value={dateValue}
            className="rounded-lg"
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {!chartData?.length && (
          <div className="py-4 flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {!isLoading && chartData?.length > 0 && (
          <BalanceChart
            height="350"
            fill={false}
            type="line"
            data={chartData}
            colors={['#1E6091', '#48CAE4', '#56C596', '#F4C430', '#B276E2']}
            options={chartOptions}
          />
        )}
      </div>
    </InfoCard>
  );
};
